import React, { useEffect, useRef, useState } from "react";
import { ACTIVITY_STATUS, ALLOWED_FILES_REGEX, AUDIT_STATUS, STATUS_MAPPING } from "../../common/Constants";
import { validateFileWithContent, FILE_TYPE_GROUPS, FILE_SIZE_LIMITS, generateAcceptAttribute, VALIDATION_MESSAGES } from "../../../utils/fileValidation";
import { useGetActivityDocuments } from "../../../backend/query";
import Icon from "../../common/Icon";
import { checkAuditorActivityStatus, download, downloadWithPresignedUrl, extractTextContent } from "../../../utils/common";
import "./ActivityModalV2.css";
import * as api from "../../../backend/request";
import { toast } from 'react-toastify';
import Select from 'react-select';
import DatePicker from "react-datepicker";
import PageLoader from "../../shared/PageLoader";
import { Alert } from "react-bootstrap";
import { ACTIVITY_TYPE, EDITOR_CONFIG } from "../../../utils/constants";
import { AxiosError } from "axios";
import ReactQuill, { Quill } from "react-quill";
import cloudUpload from "../../../assets/img/icon/cloudUpload.svg";

// Ensure links without a protocol open correctly instead of being treated as relative paths
const QuillLink = Quill.import('formats/link') as any;
QuillLink.sanitize = function(url: string) {
    if (url && !/^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(url)) {
        return 'https://' + url;
    }
    return url;
};
Quill.register(QuillLink, true);

const FORM_STATUSES = [
    { value: ACTIVITY_STATUS.APPROVE, label: 'Approve' },
    { value: ACTIVITY_STATUS.REJECT, label: 'Reject' }
]

const AUDIT_STATUSES = [
    { value: AUDIT_STATUS.COMPLIANT, label: STATUS_MAPPING[AUDIT_STATUS.COMPLIANT] },
    { value: AUDIT_STATUS.NON_COMPLIANCE, label: STATUS_MAPPING[AUDIT_STATUS.NON_COMPLIANCE] },
    { value: AUDIT_STATUS.NOT_APPLICABLE, label: STATUS_MAPPING[AUDIT_STATUS.NOT_APPLICABLE] }
]

function StatusTmp({ status }: any) {
    return (
        <span className={`status-${status}`}>{STATUS_MAPPING[status]}</span>
    )
}

function ActivityModalV2({ activity = {}, onClose, onSubmit, auditStatus: auditorAuditStatus }: any) {
    const [formStatus] = useState(checkAuditorActivityStatus(activity, 'todo', auditorAuditStatus));
    const [submitting, setSubmitting] = useState(false);
    const [auditStatus, setAuditStatus] = useState<any>(
        AUDIT_STATUSES.find((x: any) => x.value === activity.audit_status) || null
    );
    const [status, setStatus] = useState<any>(FORM_STATUSES.find((x: any) => x.value === activity.status));
    const [auditRemarks, setAuditRemarks] = useState(activity.audit_remarks);

    const [formsStatusRemarks, setFormsStatusRemarks] = useState(activity.forms_status_remarks);
    const [dueDate, setDueDate] = useState<any>(new Date(activity.due_date));
    const { documents, invalidate } = useGetActivityDocuments(activity.id);
    const [file, setFile] = useState<any>(null);
    const [invalidFile, setInvalidFile] = useState(false);
    const [error, setError] = useState("");
    const fileInputRef = useRef<HTMLInputElement>(null);
    const editorConfig = EDITOR_CONFIG;
    const Block = Quill.import('blots/block');
    Block.tagName = 'DIV';
    Quill.register(Block, true);

    function isInvalid() {
      const safeFormsStatusRemarks = extractTextContent(formsStatusRemarks || "").trim();
      const safeAuditRemarks = extractTextContent(auditRemarks || "").trim();
      if (!status || !status.value) return true;

    if (status.value === ACTIVITY_STATUS.APPROVE) {
        if (!auditStatus || !auditStatus.value) return true;
        if (auditStatus.value === AUDIT_STATUS.NOT_APPLICABLE && safeFormsStatusRemarks.length < 10) return true;
    }

    if (status.value === ACTIVITY_STATUS.REJECT) {
        if (activity.auditted !== ACTIVITY_TYPE.PHYSICAL_AUDIT && (!auditStatus || !auditStatus.value)) return true;
        if (auditStatus && auditStatus.value === AUDIT_STATUS.NON_COMPLIANCE) {
            if (!safeAuditRemarks) return true;
            if (!safeFormsStatusRemarks) return true;
            if (!dueDate) return true;
        }
        else if (auditStatus && auditStatus.value === AUDIT_STATUS.NOT_APPLICABLE) {
          if (!safeFormsStatusRemarks) return true;
      }
    }

    return false;
    }

    function getValidationErrors(): string[] {
        const safeFormsStatusRemarks = extractTextContent(formsStatusRemarks || "").trim();
        const safeAuditRemarks = extractTextContent(auditRemarks || "").trim();
        const errors: string[] = [];

        if (!status || !status.value) {
            errors.push("Please select an Evidence Status.");
            return errors;
        }

        if (status.value === ACTIVITY_STATUS.APPROVE) {
            if (!auditStatus || !auditStatus.value) {
                errors.push("Please select a Compliance Status.");
            } else if (auditStatus.value === AUDIT_STATUS.NOT_APPLICABLE && safeFormsStatusRemarks.length < 10) {
                errors.push("Observations must be at least 10 characters long.");
            }
        }

        if (status.value === ACTIVITY_STATUS.REJECT) {
            if (activity.auditted !== ACTIVITY_TYPE.PHYSICAL_AUDIT && (!auditStatus || !auditStatus.value)) {
                errors.push("Please select a Compliance Status.");
            } else if (auditStatus && auditStatus.value === AUDIT_STATUS.NON_COMPLIANCE) {
                if (!safeFormsStatusRemarks) errors.push("Observations are required.");
                if (!safeAuditRemarks) errors.push("Recommendations are required for Non-Compliance.");
                if (!dueDate) errors.push("Vendor Due Date is required for Non-Compliance.");
            } else if (auditStatus && auditStatus.value === AUDIT_STATUS.NOT_APPLICABLE) {
                if (!safeFormsStatusRemarks) errors.push("Observations are required.");
            }
        }

        return errors;
    }

    function handleSubmit() {
        const errors = getValidationErrors();
        if (errors.length > 0) {
            errors.forEach((msg) => toast.error(msg));
            return;
        }

        setSubmitting(true);
        const payload: any = {
          todo: {
            audit_remarks: auditRemarks || "",
            audit_status: (auditStatus || {}).value || "",
            forms_status_remarks: formsStatusRemarks || "",
            status: status.value,
            audited_date: new Date().toISOString()
          }
        };
        if([ACTIVITY_STATUS.REJECT].includes(status.value)){
          payload.todo.due_date = new Date(dueDate).toISOString();
        }

        if (activity.vendor_registration_id) {
          api
            .put(`/api/v1/todos/${activity.id}`, payload)
            .then((response: any) => {
              if (response instanceof AxiosError){
                  const data = (response.response || {}).data;
                  const error = (data || {}).error || "Activity updated failed. Please try again";
                  toast.error(error);
              } else {
                toast.success("Activity updated successfully.");
                onSubmit();
                onClose();
              }
            })
            .finally(() => setSubmitting(false));
        } else {
          api
            .put(`/api/v1/todos/${activity.id}`, payload)
            .then((response) => {
              if (response instanceof AxiosError){
                  const data = (response.response || {}).data;
                  const error = (data || {}).error || "Activity updated failed. Please try again";
                  toast.error(error);
              } else {
                toast.success("Activity updated successfully.");
                onSubmit();
                onClose();
              }
            })
            .finally(() => setSubmitting(false));
        }
      }

    async function onFileChange(event: any) {
        const selectedFile = event.target.files[0];

        if (!selectedFile) {
            setFile(null);
            setInvalidFile(false);
            return;
        }

        setInvalidFile(false);

        const validation = await validateFileWithContent(
            selectedFile,
            FILE_TYPE_GROUPS.ALL_DOCUMENTS,
            FILE_SIZE_LIMITS.LARGE
        );

        if (!validation.isValid) {
            setInvalidFile(true);
            setFile(null);
            toast.error(validation.error);
            event.target.value = '';
            return;
        }

        setFile(selectedFile);
        setInvalidFile(false);
    }

    function clearFile() {
        setFile(null);
        setInvalidFile(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
    }

    function uploadFile() {
        setSubmitting(true);
        const formData = new FormData();
        formData.append('file', file, file.name);
        api.post(`/api/v1/todo_details/upload?todo_id=${activity.id}`, formData).then(() => {
            setFile(null);
            setInvalidFile(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
            invalidate();
            toast.success('File uploaded successfully.');
        }).finally(() => setSubmitting(false));
    }

    useEffect(() => {
        if (status) {
          if (status.value === ACTIVITY_STATUS.REJECT) {
          }

          if (status.value === ACTIVITY_STATUS.APPROVE) {
          }
        }
      }, [status, auditStatus]);

      useEffect(() => {
        if (auditStatus && auditStatus.value === AUDIT_STATUS.NOT_APPLICABLE) {
        }
      }, [auditStatus, status]);

      useEffect(() => {
        if (status) {
          setDueDate(new Date(activity.due_date));
        }
      }, [status]);

      useEffect(() => {
        if (!dueDate) {
          setDueDate(new Date(activity.due_date));
        }
      }, [dueDate]);

      function onStatusChange(event: any) {
        setStatus(event);
        if(event && event.value) {
          setDueDate(new Date(activity.due_date));
          setAuditStatus(null);
        }
      }

    return (
        <>
          <div className="la-drawer-backdrop open" />
          <div className="la-drawer activity-edit-drawer open">
            <div className="la-drawer-head">
              <div className="la-drawer-title">Activity</div>
              <button className="la-drawer-close" onClick={onClose} aria-label="Close">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            <div className="ae-body">
              <div className="ae-section">

                {(formStatus || {}).message && (
                  <div className="ae-row" style={{ display: "block", padding: "10px 0" }}>
                    <div className={`alert alert-${(formStatus as any).type}`} style={{ margin: 0 }}>
                      {(formStatus as any).message}
                    </div>
                  </div>
                )}

                <div className="ae-row">
                  <label>Act</label>
                  <div className="ae-val">
                    <div>{(activity.act || {}).name || '—'}</div>
                    {(activity.act || {}).establishment_type && (
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{(activity.act || {}).establishment_type}</div>
                    )}
                  </div>
                </div>

                <div className="ae-row">
                  <label>Rule</label>
                  <div className="ae-val">
                    <div>{(activity.rule || {}).name || '—'}</div>
                    {((activity.rule || {}).unique_identifier || (activity.rule || {}).section_no || (activity.rule || {}).rule_no) && (
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {(activity.rule || {}).unique_identifier && (
                          <span><span style={{ fontWeight: 600 }}>ID:</span> {(activity.rule || {}).unique_identifier}</span>
                        )}
                        {(activity.rule || {}).section_no && (
                          <span><span style={{ fontWeight: 600 }}>Section No:</span> {(activity.rule || {}).section_no}</span>
                        )}
                        {(activity.rule || {}).rule_no && (
                          <span><span style={{ fontWeight: 600 }}>Rule No:</span> {(activity.rule || {}).rule_no}</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="ae-row">
                  <label>Forms/Registers &amp; Returns</label>
                  <div className="ae-val">
                    <div>{(activity.activity || {}).name || '—'}</div>
                    {(activity.activity || {}).activity_type && (
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{(activity.activity || {}).activity_type}</div>
                    )}
                  </div>
                </div>

                <div className="ae-row">
                  <label>Activity Type</label>
                  <div className="ae-val">{(activity.activity || {}).activity_type || '—'}</div>
                </div>

                <div className="ae-row">
                  <label>Month (Year)</label>
                  <div className="ae-val">
                    {activity.month ? `${activity.month} (${activity.year})` : '—'}
                  </div>
                </div>

                <div className="ae-row">
                  <label>Evidence Status</label>
                  <div className="ae-val">
                    {activity.status ? <StatusTmp status={activity.status} /> : '—'}
                  </div>
                </div>

                <div className="ae-row">
                  <label>Audit Type</label>
                  <div className="ae-val">{activity.auditted || ACTIVITY_TYPE.AUDIT}</div>
                </div>

                <div className="ae-row">
                  <label>Auditee Remarks</label>
                  <div className="ae-val">{activity.auditee_remarks || '—'}</div>
                </div>

                <div className="ae-row">
                  <label>Compliance Status</label>
                  <div className="ae-val">{activity.audit_status || '—'}</div>
                </div>

                <div className="ae-row">
                  <label>Observations</label>
                  <div className="ae-val">
                    <div dangerouslySetInnerHTML={{ __html: activity.forms_status_remarks || "" }} />
                  </div>
                </div>

                <div className="ae-row">
                  <label>Recommendations</label>
                  <div className="ae-val">
                    <div dangerouslySetInnerHTML={{ __html: activity.audit_remarks || "" }} />
                  </div>
                </div>

                {(formStatus || {}).editable && (
                  <>
                    <div className="ae-row">
                      <label>Evidence Status<span className="required">*</span></label>
                      <Select
                        classNamePrefix="select"
                        placeholder="Evidence Status"
                        options={FORM_STATUSES}
                        onChange={onStatusChange}
                        value={status}
                      />
                    </div>

                    {(status || {}).value === ACTIVITY_STATUS.APPROVE && (
                      <div className="ae-row">
                        <label>Compliance Status<span className="required">*</span></label>
                        <Select
                          classNamePrefix="select"
                          placeholder="Compliance Status"
                          options={
                            AUDIT_STATUSES.filter((option) =>
                              [AUDIT_STATUS.COMPLIANT, AUDIT_STATUS.NOT_APPLICABLE].includes(option.value)
                            )
                          }
                          onChange={setAuditStatus}
                          value={auditStatus}
                        />
                      </div>
                    )}

                    {(status || {}).value === ACTIVITY_STATUS.REJECT &&
                      activity.auditted !== ACTIVITY_TYPE.PHYSICAL_AUDIT && (
                        <div className="ae-row">
                          <label>Compliance Status<span className="required">*</span></label>
                          <Select
                            classNamePrefix="select"
                            placeholder="Compliance Status"
                            options={
                              AUDIT_STATUSES.filter((option) =>
                                [AUDIT_STATUS.NON_COMPLIANCE, AUDIT_STATUS.NOT_APPLICABLE].includes(option.value)
                              )
                            }
                            onChange={setAuditStatus}
                            value={auditStatus}
                          />
                        </div>
                      )}

                    <div className="ae-row" style={{ flexDirection: "column", alignItems: "stretch" }}>
                      <label>
                        Observations
                        {auditStatus &&
                          [
                            AUDIT_STATUS.NON_COMPLIANCE,
                            AUDIT_STATUS.NOT_APPLICABLE,
                            ACTIVITY_STATUS.REJECT,
                          ].includes(auditStatus.value) && (
                            <span className="required">*</span>
                          )}
                      </label>
                      <ReactQuill
                        theme="snow"
                        className={`form-control ${auditStatus &&
                          (status || {}).value === ACTIVITY_STATUS.APPROVE &&
                          auditStatus.value === AUDIT_STATUS.NOT_APPLICABLE &&
                          extractTextContent(formsStatusRemarks || "").length < 10
                            ? 'is-invalid' : ''}`}
                        modules={editorConfig.modules}
                        formats={editorConfig.formats}
                        value={formsStatusRemarks}
                        onChange={setFormsStatusRemarks}
                        id="quillContainer_formsStatusRemarks"
                        bounds=".ae-body"
                      />
                      {auditStatus &&
                        (status || {}).value === ACTIVITY_STATUS.APPROVE &&
                        auditStatus.value === AUDIT_STATUS.NOT_APPLICABLE && (
                          <div className={`mt-1 ${extractTextContent(formsStatusRemarks || "").length < 10 ? "text-danger" : "text-primary"}`}>
                            {extractTextContent(formsStatusRemarks || "").length < 10 ? `At least 10 characters required` : ``}
                          </div>
                        )}
                    </div>

                    {(status || {}).value === ACTIVITY_STATUS.APPROVE && (
                      <div className="ae-row" style={{ flexDirection: "column", alignItems: "stretch" }}>
                        <label>Recommendations</label>
                        <ReactQuill
                          theme="snow"
                          className="form-control"
                          modules={editorConfig.modules}
                          formats={editorConfig.formats}
                          value={auditRemarks}
                          onChange={setAuditRemarks}
                          id="quillContainer_auditRemarks"
                          bounds=".ae-body"
                        />
                      </div>
                    )}

                    {(status || {}).value === ACTIVITY_STATUS.REJECT &&
                      activity.auditted !== ACTIVITY_TYPE.PHYSICAL_AUDIT && (
                        <>
                          <div className="ae-row" style={{ flexDirection: "column", alignItems: "stretch" }}>
                            <label>
                              Recommendations
                              {[AUDIT_STATUS.NON_COMPLIANCE].includes(
                                (auditStatus || {}).value
                              ) && <span className="required">*</span>}
                            </label>
                            <ReactQuill
                              theme="snow"
                              className="form-control"
                              modules={editorConfig.modules}
                              formats={editorConfig.formats}
                              value={auditRemarks}
                              onChange={setAuditRemarks}
                              id="quillContainer_auditRemarks_reject"
                              bounds=".ae-body"
                            />
                          </div>
                          <div className="ae-row">
                            <label>
                              Vendor Due Date
                              {[AUDIT_STATUS.NON_COMPLIANCE].includes(
                                (auditStatus || {}).value
                              ) && <span className="required">*</span>}
                            </label>
                            <DatePicker
                              className="ae-date"
                              selected={dueDate}
                              dateFormat="dd-MM-yyyy"
                              onChange={setDueDate}
                              placeholderText="dd-mm-yyyy"
                              minDate={new Date()}
                              showMonthDropdown
                              showYearDropdown
                              dropdownMode="select"
                            />
                          </div>
                        </>
                      )}
                  </>
                )}

                <div className="ae-evidence-header">Evidence</div>

                {/* File upload — only for editable physical audit */}
                {(formStatus || {}).editable && activity.auditted === ACTIVITY_TYPE.PHYSICAL_AUDIT && (
                  <>
                    <input
                      ref={fileInputRef}
                      type="file"
                      style={{ display: 'none' }}
                      onChange={onFileChange}
                      accept={generateAcceptAttribute(FILE_TYPE_GROUPS.ALL_DOCUMENTS)}
                    />
                    {!file ? (
                      <div className="ae-upload-box" onClick={() => { if (fileInputRef.current) fileInputRef.current.click(); }}>
                        <div className="mb-2">
                          <img src={cloudUpload} alt="upload" />
                        </div>
                        <p className="mb-2">Click to browse and upload</p>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); if (fileInputRef.current) fileInputRef.current.click(); }}
                        >
                          Choose File
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="ae-upload-success">
                          <span className="ae-upload-filename">{file.name}</span>
                          <button className="ae-upload-remove" onClick={clearFile} type="button">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M10.0002 5.99992L6.00016 9.99992M6.00016 5.99992L10.0002 9.99992M14.6668 7.99992C14.6668 11.6818 11.6821 14.6666 8.00016 14.6666C4.31826 14.6666 1.3335 11.6818 1.3335 7.99992C1.3335 4.31802 4.31826 1.33325 8.00016 1.33325C11.6821 1.33325 14.6668 4.31802 14.6668 7.99992Z" stroke="#757575" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                        </div>
                        <button
                          className="ae-upload-btn"
                          disabled={invalidFile || submitting}
                          onClick={uploadFile}
                          type="button"
                        >
                          {submitting ? 'Uploading...' : 'Upload'}
                        </button>
                      </>
                    )}
                    {invalidFile && (
                      <div className="text-danger mt-1">
                        <small>{VALIDATION_MESSAGES.INVALID_FILE_ALL_DOCS}</small>
                      </div>
                    )}
                  </>
                )}

                {/* File listing — always visible */}
                <div style={{ marginTop: 16 }}>
                  <table className="table modalTable fixed_header">
                    <thead>
                      <tr>
                        <th style={{ width: "75%" }}>File Name</th>
                        <th style={{ width: "25%" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody style={{ height: "300px" }}>
                      {documents.map((file: any, index: number) => (
                        <tr key={index}>
                          <td width="75%" style={{ overflowWrap: "break-word" }}>{file.file_name}</td>
                          <td width="25%">
                            <div className="d-flex flex-row align-items-center">
                              <Icon
                                name={"download"}
                                action={() => downloadWithPresignedUrl(file.file_name, file.file_path)}
                                text="Download"
                              />
                            </div>
                          </td>
                        </tr>
                      ))}
                      {documents.length === 0 && (
                        <tr>
                          <td colSpan={2} rowSpan={4} width="100%" style={{ height: "200px", border: 0 }}>
                            <div className="d-flex w-100 h-100 align-items-center justify-content-center">
                              No files available
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

              </div>
            </div>

            <div className="ae-foot">
              {(formStatus || {}).editable ? (
                <>
                  <button className="ae-back" onClick={onClose}>Back</button>
                  <button
                    className="ae-submit"
                    onClick={handleSubmit}
                    disabled={submitting}
                    style={isInvalid() ? { opacity: 0.6, cursor: "not-allowed" } : undefined}
                  >
                    {submitting ? "Submitting..." : "Submit"}
                  </button>
                </>
              ) : (
                <button className="ae-submit" onClick={onClose}>
                  Close
                </button>
              )}
            </div>
          </div>

          {submitting && <PageLoader />}
        </>
    );
}

export default ActivityModalV2;
